import { ref, reactive, onMounted } from 'vue'
import { baserowService } from '@/services/baserow'

interface CharteColors {
  primary: string
  secondary: string
  accent: string
  neutral: string
  success: string
  warning: string
  error: string
  [key: string]: string
}

interface CharteData {
  colors: CharteColors
  logo: string | null
  fonts: string[]
  isLoaded: boolean
  error: string | null
}

const charteData = reactive<CharteData>({
  colors: {
    primary: '#f39200', // Breizhteam orange default
    secondary: '#000000', // Black default
    accent: '#f39200',
    neutral: '#6b7280',
    success: '#22c55e',
    warning: '#f59e0b',
    error: '#ef4444',
  },
  logo: null,
  fonts: ['Inter', 'Roboto', 'sans-serif'],
  isLoaded: false,
  error: null
})

let isLoading = false

export function useCharteGraphique() {
  const loading = ref(isLoading)

  const loadCharte = async () => {
    if (charteData.isLoaded || isLoading) {
      return charteData
    }

    try {
      isLoading = true
      loading.value = true
      charteData.error = null

      console.log('🎨 Loading charte graphique from Baserow...')
      
      const completeCharte = await baserowService.getCompleteCharte()
      
      // Update reactive data
      Object.assign(charteData.colors, completeCharte.colors)
      charteData.logo = completeCharte.logo
      charteData.fonts = completeCharte.fonts
      charteData.isLoaded = true

      console.log('✅ Charte graphique loaded successfully:', {
        colors: charteData.colors,
        logo: charteData.logo,
        fonts: charteData.fonts
      })

    } catch (error) {
      console.error('❌ Error loading charte graphique:', error)
      charteData.error = error instanceof Error ? error.message : 'Erreur de chargement de la charte'
      
      // Keep default values on error
    } finally {
      isLoading = false
      loading.value = false
    }

    return charteData
  }

  const refreshCharte = async () => {
    charteData.isLoaded = false
    return await loadCharte()
  }

  // CSS Custom Properties helpers
  const getCSSVariables = () => {
    const cssVars: { [key: string]: string } = {}
    
    Object.entries(charteData.colors).forEach(([key, value]) => {
      cssVars[`--color-${key}`] = value
    })
    
    if (charteData.fonts.length > 0) {
      cssVars['--font-family'] = charteData.fonts.join(', ')
    }
    
    return cssVars
  }

  const applyCSSVariables = () => {
    if (typeof document !== 'undefined') {
      const root = document.documentElement
      const cssVars = getCSSVariables()
      
      Object.entries(cssVars).forEach(([key, value]) => {
        root.style.setProperty(key, value)
      })
      
      console.log('🎨 Applied CSS variables:', cssVars)
    }
  }

  // Tailwind color helpers
  const getTailwindColors = () => {
    return {
      'primary-500': charteData.colors.primary,
      'primary-600': charteData.colors.primary,
      'primary-700': charteData.colors.primary,
      'secondary-500': charteData.colors.secondary,
      'accent-500': charteData.colors.accent,
    }
  }

  // Auto-load on first use
  onMounted(() => {
    if (!charteData.isLoaded && !isLoading) {
      loadCharte().then(() => {
        applyCSSVariables()
      })
    }
  })

  return {
    charte: charteData,
    loading,
    loadCharte,
    refreshCharte,
    getCSSVariables,
    applyCSSVariables,
    getTailwindColors
  }
}